import type { StepAttempt, StepDisplayState, ThreadDetail } from "../../../api/threads/liveMessages";
import type { WorkflowGraph } from "./workflowGraph";

/** What a node shows of one Run (WD-4): its state as module C shows it, a step's latest attempt, a group's count. */
export interface NodeRun {
  readonly state: StepDisplayState;
  /** `null` for a sub-workflow, and for a step before its first request. */
  readonly attempt: StepAttempt | null;
  /** A sub-workflow's steps done or skipped, of all of them; `null` for a step. */
  readonly group: { readonly settled: number; readonly steps: number } | null;
}

/** A Run on the graph's nodes, by node ID. */
export type GraphRun = ReadonlyMap<string, NodeRun>;

/**
 * Module C's step detail and sub-workflow states (W-22, WD-4) on the graph's nodes: a step
 * node takes its step's, a sub-workflow node its group's. Only a lookup by key; C decides
 * every state, so a collapsed group says what C says of it.
 */
export function graphRun(graph: WorkflowGraph, detail: ThreadDetail): GraphRun {
  const steps = new Map((detail.steps ?? []).map((step) => [step.step_key, step] as const));
  const groups = new Map((detail.groups ?? []).map((group) => [group.group_key, group] as const));
  const run = new Map<string, NodeRun>();
  for (const node of graph.nodes) {
    if ((node.kind !== "step" && node.kind !== "sub_workflow") || node.progressKey === null) continue;
    if (node.kind === "step") {
      const step = steps.get(node.progressKey);
      if (step !== undefined) run.set(node.id, { state: step.display_state, attempt: step.attempt ?? null, group: null });
      continue;
    }
    const group = groups.get(node.progressKey);
    if (group !== undefined) run.set(node.id, { state: group.display_state, attempt: null, group: { settled: group.settled_steps, steps: group.steps } });
  }
  return run;
}
